// Файл app_theme.js

/**
 * Модуль хранит единый каталог тем и управляет применением палитры к корневому элементу.
 * Он нормализует идентификаторы, синхронизирует выбор между вкладками браузера и сохраняет его локально.
 * Компоненты интерфейса используют этот API вместо прямого изменения CSS-классов.
 */

import { storage } from '../../utils/storage';



const app_theme_storage_key = 'potale_app_theme';
const app_theme_event_name = 'potale:app-theme-changed';
const default_app_theme = 'blue';

const app_themes = [
    {
        id: 'light',
        title: 'Светлая',
        description: 'Нейтральный светлый интерфейс с мягкими тенями.',
    },
    {
        id: 'dark',
        title: 'Тёмная',
        description: 'Тёмные поверхности и комфортный контраст вечером.',
    },
    {
        id: 'blue',
        title: 'Синяя',
        description: 'Фирменная синяя тема Potale.',
    },
    {
        id: 'sky',
        title: 'Небесная',
        description: 'Голубой градиент и светлые карточки как в классическом ЛК.',
    },
    {
        id: 'arctic',
        title: 'Арктическая',
        description: 'Холодная светлая палитра с ледяным синим акцентом.',
    },
    {
        id: 'sakura',
        title: 'Сакура',
        description: 'Светлая пудровая тема с мягким ягодным акцентом.',
    },
    {
        id: 'sand',
        title: 'Песочная',
        description: 'Тёплая спокойная палитра песка, камня и терракоты.',
    },
    {
        id: 'midnight',
        title: 'Полночная',
        description: 'Глубокая индиго-тема с холодным голубым акцентом.',
    },
    {
        id: 'neon',
        title: 'Неоновая',
        description: 'Тёмная технологичная тема с бирюзовым акцентом.',
    },
    {
        id: 'aurora',
        title: 'Аврора',
        description: 'Тёмная северная палитра с бирюзовым и ягодным свечением.',
    },
    {
        id: 'space',
        title: 'Космос — галактика',
        description: 'Фотографический космос с тёмным стеклянным интерфейсом.',
    },
    {
        id: 'space_nebula',
        title: 'Космос — туманность',
        description: 'Светлое матовое стекло поверх звёзд и галактики.',
    },
    {
        id: 'porcelain',
        title: 'Фарфоровая',
        description: 'Тёплая светлая тема с чернильно-синими акцентами.',
    },
    {
        id: 'ocean',
        title: 'Океанская',
        description: 'Глубокая морская тема с чистым бирюзовым акцентом.',
    },
    {
        id: 'copper',
        title: 'Медная',
        description: 'Тёмный графит с тёплыми медными деталями.',
    },
    {
        id: 'coral',
        title: 'Коралловая',
        description: 'Светлая кремовая палитра с мягким коралловым цветом.',
    },
    {
        id: 'graphite',
        title: 'Графитовая',
        description: 'Строгая графитовая палитра с бирюзовым акцентом.',
    },
    {
        id: 'emerald',
        title: 'Изумрудная',
        description: 'Светлая тема с зелёно-бирюзовыми акцентами.',
    },
    {
        id: 'lavender',
        title: 'Лавандовая',
        description: 'Мягкая современная палитра с фиолетовым акцентом.',
    },
    {
        id: 'mint',
        title: 'Мятная',
        description: 'Светлая свежая тема с мятным и хвойным акцентом.',
    },
    {
        id: 'plum',
        title: 'Сливовая',
        description: 'Тёмная сливовая тема с мягким розово-фиолетовым акцентом.',
    },
    {
        id: 'nord',
        title: 'Северная',
        description: 'Спокойная тёмная тема в холодных северных оттенках.',
    },
    {
        id: 'solar',
        title: 'Солнечная',
        description: 'Светлая тёплая тема с золотистым и медовым акцентом.',
    },
    {
        id: 'rosewood',
        title: 'Палисандровая',
        description: 'Тёмная древесная тема с бордовым и медным акцентом.',
    },
    {
        id: 'cloud',
        title: 'Облачная',
        description: 'Воздушная светлая тема с мягким серо-голубым оформлением.',
    },
    {
        id: 'steel',
        title: 'Стальная',
        description: 'Сдержанная графитово-стальная тема с холодным синим акцентом.',
    },
    {
        id: 'amber',
        title: 'Янтарная',
        description: 'Тёплая янтарная тема с глубоким шоколадным текстом.',
    },
    {
        id: 'aqua',
        title: 'Аквамариновая',
        description: 'Светлая морская тема с аквамариновым и синим акцентом.',
    },
    {
        id: 'monochrome',
        title: 'Монохромная',
        description: 'Контрастная чёрно-белая тема без лишних цветовых отвлечений.',
    },
];

const app_theme_ids = new Set(app_themes.map((theme) => theme.id));

const normalize_app_theme = (theme_id) => (
    app_theme_ids.has(String(theme_id || '').trim())
        ? String(theme_id).trim()
        : default_app_theme
);

const read_app_theme = () => normalize_app_theme(
    storage.get_item(app_theme_storage_key)
);

const apply_app_theme = (theme_id, options = {}) => {
    const normalized_theme = normalize_app_theme(theme_id);
    const root = document.documentElement;

    root.dataset.appTheme = normalized_theme;
    root.style.colorScheme = (
        [
            'dark',
            'graphite',
            'midnight',
            'neon',
            'aurora',
            'space',
            'ocean',
            'copper',
            'plum',
            'nord',
            'rosewood',
            'steel',
            'monochrome',
        ].includes(normalized_theme)
            ? 'dark'
            : 'light'
    );

    if (options.persist !== false) {
        storage.set_item(app_theme_storage_key, normalized_theme);
    }

    window.dispatchEvent(new CustomEvent(app_theme_event_name, {
        detail: { theme_id: normalized_theme },
    }));

    return normalized_theme;
};

const initialize_app_theme = () => {
    const theme_id = apply_app_theme(read_app_theme(), { persist: false });

    window.addEventListener('storage', (event) => {
        if (event.key === app_theme_storage_key) {
            apply_app_theme(read_app_theme(), { persist: false });
        }
    });

    return theme_id;
};

const subscribe_app_theme = (listener) => {
    const handle_theme_change = (event) => {
        listener(normalize_app_theme(event?.detail?.theme_id));
    };

    window.addEventListener(app_theme_event_name, handle_theme_change);

    return () => {
        window.removeEventListener(app_theme_event_name, handle_theme_change);
    };
};


export {
    app_themes,
    apply_app_theme,
    default_app_theme,
    initialize_app_theme,
    normalize_app_theme,
    read_app_theme,
    subscribe_app_theme,
};
